import { useMemo } from "react";
import { ListItem } from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { FieldValues, ArrayPath, Path, PathValue } from "react-hook-form";
import { ObjectSchema } from "yup";
import TooltipIconButton from "components/Shared/TooltipIconButton";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import { BaseFilterFormProps } from "./utils";
import { getAppliedFilterSchemaForFieldName, getSchemaForDef } from "./schema";
import FilterSelect from "./FilterSelect";
import VariantSelect from "./VariantSelect";
import FilterInput from "./FilterInput";

type Props<
  TFieldValues extends FieldValues,
  TName extends ArrayPath<TFieldValues> = ArrayPath<TFieldValues>,
> = BaseFilterFormProps<TFieldValues, TName> & {
  index: number;
  onRemove?: (index: number) => void;
};

export default function Filter<
  TFieldValues extends FieldValues,
  TName extends ArrayPath<TFieldValues> = ArrayPath<TFieldValues>,
>({
  filterDefinitions,
  filtersFormName,
  formContext,
  index,
  onRemove,
}: Props<TFieldValues, TName>) {
  type TValue = PathValue<TFieldValues, Path<TFieldValues>>;

  const schema = useMemo(
    () =>
      getAppliedFilterSchemaForFieldName(
        filtersFormName,
        filterDefinitions,
      ) as unknown as ObjectSchema<TFieldValues>,
    [filtersFormName, filterDefinitions],
  );

  const { ControlledFormField } = useMemo(
    () => createFormFieldComponents<TFieldValues>({ schema }),
    [schema],
  );

  const filterName = `${filtersFormName}.${index}.filter` as Path<TFieldValues>;
  const variantName =
    `${filtersFormName}.${index}.variant` as Path<TFieldValues>;
  const valueName = `${filtersFormName}.${index}.value` as Path<TFieldValues>;

  const resetValue = () =>
    formContext.resetField(valueName, {
      defaultValue: null as TValue,
    });

  const [selectedFilterId, selectedVariantId] = formContext.watch([
    filterName,
    variantName,
  ]);

  const selectedFilter = useMemo(
    () => filterDefinitions.find((fd) => fd.id === selectedFilterId),
    [filterDefinitions, selectedFilterId],
  );

  const selectedVariant = useMemo(
    () =>
      selectedFilter?.variants?.find(
        (variant) => variant.id === selectedVariantId,
      ),
    [selectedFilter, selectedVariantId],
  );

  return (
    <ListItem
      display="flex"
      alignItems="flex-start"
      my={4}
      _first={{ mt: 0 }}
      _last={{ mb: 0 }}
    >
      <ControlledFormField
        name={filterName}
        label={null}
        renderInput={({ field: { onChange, ...field } }) => (
          <FilterSelect
            {...field}
            onChange={(newFilterId) => {
              onChange(newFilterId);
              const newFilterDef = filterDefinitions.find(
                (fd) => fd.id === newFilterId,
              );
              formContext.resetField(
                `${filtersFormName}.${index}` as Path<TFieldValues>,
                {
                  defaultValue: {
                    filter: newFilterId,
                    variant: (newFilterDef?.defaultVariant ||
                      newFilterDef?.variants?.[0]?.id) as TValue,
                    value: null,
                  } as TValue,
                  keepDirty: true,
                  keepTouched: true,
                  keepError: false,
                },
              );
            }}
            filterDefinitions={filterDefinitions}
          />
        )}
        my={0}
        flex="1 0 10em"
        minW="12.5em"
      />

      <ControlledFormField
        key={String(selectedFilterId)}
        name={variantName}
        label={null}
        isDisabled={!selectedFilter}
        renderInput={({ field: { onChange, ...field } }) => (
          <VariantSelect
            {...field}
            onChange={(newVariantId) => {
              onChange(newVariantId);

              if (!selectedFilter || !selectedVariant) {
                return resetValue();
              }

              const newVariantDef = selectedFilter?.variants?.find(
                (fd) => fd.id === newVariantId,
              );

              if (!newVariantDef) return resetValue();

              const oldSchema = getSchemaForDef(
                selectedFilter,
                selectedVariant,
                false,
              );
              const newSchema = getSchemaForDef(
                selectedFilter,
                newVariantDef,
                false,
              );

              if (oldSchema?.type !== newSchema?.type) {
                resetValue();
              }
            }}
            isDisabled={!selectedFilter}
            variants={selectedFilter?.variants}
          />
        )}
        my={0}
        mx={2}
        flex="1 0 12.5em"
        minW="12.5em"
      />

      <FilterInput
        filterDefinition={selectedFilter}
        filterVariantDefinition={selectedVariant}
        fieldName={valueName}
        formContext={formContext}
        schema={schema}
        label={null}
        isDisabled={!selectedFilter || !selectedVariant}
        my={0}
        flex="4 0 10em"
      />

      {onRemove && (
        <TooltipIconButton
          label="Remove filter"
          icon={<DeleteIcon />}
          variant="ghost"
          colorScheme="red"
          sx={{ "&:not(:hover)": { color: "gray.300" } }}
          size="xs"
          onClick={() => onRemove(index)}
          tooltipProps={{ openDelay: 400 }}
          mt={3}
          ml={2}
        />
      )}
    </ListItem>
  );
}
